import React, { useEffect, useState, useCallback } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  Table,
  Button,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Form,
  FormGroup,
  Label,
  Input,
  Alert,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane
} from "reactstrap";
import Breadcrumbs from "../../components/Common/Breadcrumb";
import { withTranslation } from "react-i18next";
import classnames from "classnames";

const apiUrl = process.env.REACT_APP_API_URL;

const SellerCommission = (props) => {
  const [activeTab, setActiveTab] = useState("1");
  const [sellerData, setSellerData] = useState([]);
  const [managerData, setManagerData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingManagers, setLoadingManagers] = useState(false);
  const [modal, setModal] = useState(false);
  const [managerModal, setManagerModal] = useState(false);
  const [currentSeller, setCurrentSeller] = useState({});
  const [currentManager, setCurrentManager] = useState({});
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [managerSuccessMessage, setManagerSuccessMessage] = useState("");
  const [managerErrorMessage, setManagerErrorMessage] = useState("");

  // Toggle tabs
  const toggle = tab => {
    if (activeTab !== tab) setActiveTab(tab);
  };

  // Create memoized fetchSellerData function with useCallback
  const fetchSellerData = useCallback(async () => {
    setLoading(true);
    try {
      const authUser = JSON.parse(localStorage.getItem("authUser"));
      const token = authUser?.token;
      if (!token) throw new Error("Authentication token not found");

      const response = await fetch(`${apiUrl}/api/data/seller-rates`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!response.ok) throw new Error("Failed to fetch seller data");

      const data = await response.json();
      
      // Ensure all numeric fields are properly parsed to numbers
      const enhancedData = data.map(seller => ({
        ...seller,
        rate: parseFloat(seller.rate) || 0
      }));
      
      setSellerData(enhancedData);
    } catch (error) {
      console.error("Error fetching seller data:", error);
      // Mock data for development/testing
      setSellerData([]);
    } finally {
      setLoading(false);
    }
  }, [apiUrl]);

  // Create memoized fetchManagerData function with useCallback
  const fetchManagerData = useCallback(async () => {
    setLoadingManagers(true);
    try {
      const authUser = JSON.parse(localStorage.getItem("authUser"));
      const token = authUser?.token;
      if (!token) throw new Error("Authentication token not found");

      const response = await fetch(`${apiUrl}/api/data/manager-rates`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!response.ok) throw new Error("Failed to fetch manager data");

      const data = await response.json();
      
      // Ensure all numeric fields are properly parsed to numbers
      const enhancedData = data.map(manager => ({
        ...manager,
        percentage: parseFloat(manager.percentage) || 0
      }));
      
      setManagerData(enhancedData);
    } catch (error) {
      console.error("Error fetching manager data:", error);
      // Mock data for development/testing
      setManagerData([]);
    } finally {
      setLoadingManagers(false);
    }
  }, [apiUrl]);

  // Handle seller modal toggle
  const toggleSellerModal = () => {
    setModal(!modal);
    // Clear messages when toggling modal
    setSuccessMessage("");
    setErrorMessage("");
  };

  // Handle manager modal toggle
  const toggleManagerModal = () => {
    setManagerModal(!managerModal);
    // Clear messages when toggling modal
    setManagerSuccessMessage("");
    setManagerErrorMessage("");
  };

  // Handle edit seller rate
  const handleEditSeller = (seller) => {
    setCurrentSeller({
      ...seller,
      rate: parseFloat(seller.rate) || 0
    });
    setModal(true);
  };

  // Handle edit manager rate
  const handleEditManager = (manager) => {
    setCurrentManager({
      ...manager,
      percentage: parseFloat(manager.percentage) || 0
    });
    setManagerModal(true);
  };

  // Handle seller form input changes
  const handleSellerInputChange = (e) => {
    const { name, value } = e.target;
    setCurrentSeller({ 
      ...currentSeller, 
      [name]: name === "rate" ? parseFloat(value) || 0 : value 
    });
  };

  // Handle manager form input changes
  const handleManagerInputChange = (e) => {
    const { name, value } = e.target;
    setCurrentManager({ 
      ...currentManager, 
      [name]: name === "percentage" ? parseFloat(value) || 0 : value 
    });
  };

  // Handle seller form submission
  const handleSellerSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const authUser = JSON.parse(localStorage.getItem("authUser"));
      const token = authUser?.token;
      if (!token) throw new Error("Authentication token not found");

      const response = await fetch(`${apiUrl}/api/data/seller-rates/${currentSeller.id}`, {
        method: 'PUT',
        headers: { 
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}` 
        },
        body: JSON.stringify({
          rate: parseFloat(currentSeller.rate) || 0
        })
      });

      if (!response.ok) throw new Error("Failed to update seller rate");

      // Set success message
      setSuccessMessage("Seller rate updated successfully!");
      
      // Update the seller data in the table
      setSellerData(prevData => 
        prevData.map(seller => 
          seller.id === currentSeller.id ? 
            { ...seller, rate: parseFloat(currentSeller.rate) || 0 } : 
            seller
        )
      );
      
      // Close modal after a delay to show success message
      setTimeout(() => {
        toggleSellerModal();
        // Clear success message
        setSuccessMessage("");
      }, 1500);
    } catch (error) {
      console.error("Error updating seller rate:", error);
      setErrorMessage("Failed to update seller rate. Please try again.");
    }
  };

  // Handle manager form submission
  const handleManagerSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const authUser = JSON.parse(localStorage.getItem("authUser"));
      const token = authUser?.token;
      if (!token) throw new Error("Authentication token not found");

      const response = await fetch(`${apiUrl}/api/data/manager-rates/${currentManager.id}`, {
        method: 'PUT',
        headers: { 
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}` 
        },
        body: JSON.stringify({
          percentage: parseFloat(currentManager.percentage) || 0
        })
      });

      if (!response.ok) throw new Error("Failed to update manager rate");

      // Set success message
      setManagerSuccessMessage("Manager rate updated successfully!");
      
      // Update the manager data in the table
      setManagerData(prevData => 
        prevData.map(manager => 
          manager.id === currentManager.id ? 
            { ...manager, percentage: parseFloat(currentManager.percentage) || 0 } : 
            manager
        )
      );
      
      // Close modal after a delay to show success message
      setTimeout(() => {
        toggleManagerModal();
        // Clear success message
        setManagerSuccessMessage("");
      }, 1500);
    } catch (error) {
      console.error("Error updating manager rate:", error);
      setManagerErrorMessage("Failed to update manager rate. Please try again.");
    }
  };

  // Load data on component mount and when tab changes
  useEffect(() => {
    fetchSellerData();
  }, [fetchSellerData]);

  useEffect(() => {
    if (activeTab === "2") {
      fetchManagerData();
    }
  }, [activeTab, fetchManagerData]);

  // Meta title
  document.title = "Commission Rates | Sunwave";

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <Breadcrumbs title={props.t("Commission Rates")} breadcrumbItem={props.t("Commission Rates")} />

          <Row>
            <Col lg="12">
              <Card>
                <CardBody>
                  <Nav tabs>
                    <NavItem>
                      <NavLink
                        className={classnames({ active: activeTab === "1" })}
                        onClick={() => { toggle("1"); }}
                      >
                        {props.t("Seller Rates")}
                      </NavLink>
                    </NavItem>
                    <NavItem>
                      <NavLink
                        className={classnames({ active: activeTab === "2" })}
                        onClick={() => { toggle("2"); }}
                      >
                        {props.t("Manager Rates")}
                      </NavLink>
                    </NavItem>
                  </Nav>
                  
                  <TabContent activeTab={activeTab} className="pt-4">
                    {/* Seller Rates Tab */}
                    <TabPane tabId="1">
                      <h4 className="card-title mb-4">{props.t("Seller Commission Rates")}</h4>
                      <div className="table-responsive">
                        <Table className="table table-centered table-nowrap mb-0">
                          <thead className="thead-light">
                            <tr>
                              <th>{props.t("ID")}</th>
                              <th>{props.t("Seller Name")}</th>
                              <th>{props.t("Seller Email")}</th>
                              <th>{props.t("Rate ($)")}</th>
                              <th>{props.t("Actions")}</th>
                            </tr>
                          </thead>
                          <tbody>
                            {loading ? (
                              <tr>
                                <td colSpan="4" className="text-center">
                                  {props.t("Loading...")}
                                </td>
                              </tr>
                            ) : sellerData.length === 0 ? (
                              <tr>
                                <td colSpan="4" className="text-center">
                                  {props.t("No sellers found")}
                                </td>
                              </tr>
                            ) : (
                              sellerData.map((seller, index) => (
                                <tr key={index}>
                                  <td>{seller.id}</td>
                                  <td>{seller.seller_name || "-"}</td>
                                  <td>{seller.seller_email}</td>
                                  <td>${typeof seller.rate === 'number' ? seller.rate.toFixed(2) : '0.00'}</td>
                                  <td>
                                    <Button
                                      color="primary"
                                      size="sm"
                                      onClick={() => handleEditSeller(seller)}
                                    >
                                      {props.t("Edit Rate")}
                                    </Button>
                                  </td>
                                </tr>
                              ))
                            )}
                          </tbody>
                        </Table>
                      </div>
                    </TabPane>
                    
                    {/* Manager Rates Tab */}
                    <TabPane tabId="2">
                      <h4 className="card-title mb-4">{props.t("Manager Commission Rates")}</h4>
                      <div className="table-responsive">
                        <Table className="table table-centered table-nowrap mb-0">
                          <thead className="thead-light">
                            <tr>
                              <th>{props.t("ID")}</th>
                              <th>{props.t("Manager Email")}</th>
                              <th>{props.t("Rate (%)")}</th>
                              <th>{props.t("Actions")}</th>
                            </tr>
                          </thead>
                          <tbody>
                            {loadingManagers ? (
                              <tr>
                                <td colSpan="4" className="text-center">
                                  {props.t("Loading...")}
                                </td>
                              </tr>
                            ) : managerData.length === 0 ? (
                              <tr>
                                <td colSpan="4" className="text-center">
                                  {props.t("No managers found")}
                                </td>
                              </tr>
                            ) : (
                              managerData.map((manager, index) => (
                                <tr key={index}>
                                  <td>{manager.id}</td>
                                  <td>{manager.manager_email}</td>
                                  <td>{typeof manager.percentage === 'number' ? manager.percentage.toFixed(2) : '0.00'}%</td>
                                  <td>
                                    <Button
                                      color="primary"
                                      size="sm"
                                      onClick={() => handleEditManager(manager)}
                                    >
                                      {props.t("Edit Rate")}
                                    </Button>
                                  </td>
                                </tr>
                              ))
                            )}
                          </tbody>
                        </Table>
                      </div>
                    </TabPane>
                  </TabContent>
                </CardBody>
              </Card>
            </Col>
          </Row>

          {/* Edit Seller Modal */}
          <Modal isOpen={modal} toggle={toggleSellerModal}>
            <ModalHeader toggle={toggleSellerModal}>{props.t("Edit Seller Rate")}</ModalHeader>
            <Form onSubmit={handleSellerSubmit}>
              <ModalBody>
                {successMessage && (
                  <Alert color="success" className="mb-3">
                    {successMessage}
                  </Alert>
                )}
                {errorMessage && (
                  <Alert color="danger" className="mb-3">
                    {errorMessage}
                  </Alert>
                )}
                <FormGroup>
                  <Label>{props.t("Seller Email")}</Label>
                  <Input 
                    type="text" 
                    name="seller_email" 
                    value={currentSeller.seller_email || ""} 
                    disabled 
                  />
                </FormGroup>
                <FormGroup>
                  <Label>{props.t("Rate ($)")}</Label>
                  <Input 
                    type="number" 
                    step="0.01" 
                    min="0" 
                    name="rate" 
                    value={currentSeller.rate || ""} 
                    onChange={handleSellerInputChange} 
                  />
                </FormGroup>
              </ModalBody>
              <ModalFooter>
                <Button color="secondary" onClick={toggleSellerModal}>
                  {props.t("Cancel")}
                </Button>{" "}
                <Button color="primary" type="submit">
                  {props.t("Save")}
                </Button>
              </ModalFooter>
            </Form>
          </Modal>

          {/* Edit Manager Modal */}
          <Modal isOpen={managerModal} toggle={toggleManagerModal}>
            <ModalHeader toggle={toggleManagerModal}>{props.t("Edit Manager Rate")}</ModalHeader>
            <Form onSubmit={handleManagerSubmit}>
              <ModalBody>
                {managerSuccessMessage && (
                  <Alert color="success" className="mb-3">
                    {managerSuccessMessage}
                  </Alert>
                )}
                {managerErrorMessage && (
                  <Alert color="danger" className="mb-3">
                    {managerErrorMessage}
                  </Alert>
                )}
                <FormGroup>
                  <Label>{props.t("Manager Email")}</Label>
                  <Input 
                    type="text" 
                    name="manager_email" 
                    value={currentManager.manager_email || ""} 
                    disabled 
                  />
                </FormGroup>
                <FormGroup>
                <Label>{props.t("Seller Name")}</Label>
                <Input 
                  type="text" 
                  name="seller_name" 
                  value={currentSeller.seller_name || ""} 
                  disabled 
                />
              </FormGroup>
                <FormGroup>
                  <Label>{props.t("Rate (%)")}</Label>
                  <Input 
                    type="number" 
                    step="0.01" 
                    min="0" 
                    max="100" 
                    name="percentage" 
                    value={currentManager.percentage || ""} 
                    onChange={handleManagerInputChange} 
                  />
                </FormGroup>
              </ModalBody>
              <ModalFooter>
                <Button color="secondary" onClick={toggleManagerModal}>
                  {props.t("Cancel")}
                </Button>{" "}
                <Button color="primary" type="submit">
                  {props.t("Save")}
                </Button>
              </ModalFooter>
            </Form>
          </Modal>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default withTranslation()(SellerCommission);